import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { searchDevices } from '../../services/devices';
import type { Device } from '../../types/models';
import { SuggestDeviceModal } from './SuggestDeviceModal';

interface SearchBarProps {
  initialValue?: string;
  placeholder?: string;
  variant?: 'hero' | 'compact';
  className?: string;
}

export function SearchBar({
  initialValue = '',
  placeholder = 'Find products or batteries',
  variant = 'compact',
  className = '',
}: SearchBarProps) {
  const navigate = useNavigate();
  const [value, setValue] = useState(initialValue);
  const [results, setResults] = useState<Device[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const requestId = useRef(0);

  useEffect(() => {
    const query = value.trim();
    if (query.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    const id = ++requestId.current;
    setLoading(true);
    const timer = setTimeout(() => {
      searchDevices(query)
        .then((devices) => {
          if (requestId.current === id) setResults(devices.slice(0, 5));
        })
        .catch(() => {
          if (requestId.current === id) setResults([]);
        })
        .finally(() => {
          if (requestId.current === id) setLoading(false);
        });
    }, 250);
    return () => clearTimeout(timer);
  }, [value]);

  useEffect(() => {
    function onClickAway(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onClickAway);
    return () => document.removeEventListener('mousedown', onClickAway);
  }, []);

  const runDetailedSearch = (query: string) => {
    setOpen(false);
    navigate(`/?q=${encodeURIComponent(query)}`);
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const query = value.trim();
    if (!query) return;
    if (results.length === 1 && results[0]) {
      navigate(`/devices/${results[0]._id}`);
      setOpen(false);
      return;
    }
    runDetailedSearch(query);
  };

  const showDropdown = open && value.trim().length >= 2;

  return (
    <div ref={containerRef} className={`relative w-full ${className}`}>
      <form onSubmit={handleSubmit} role="search">
        <label htmlFor="search-input" className="sr-only">
          Search devices or batteries
        </label>
        <input
          id="search-input"
          type="search"
          className={variant === 'hero' ? 'pill-input text-base' : 'pill-input py-2 text-sm'}
          placeholder={placeholder}
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          autoComplete="off"
        />
      </form>

      {showDropdown && (
        <div className="absolute z-30 mt-2 w-full min-w-[280px] panel p-2 text-sm">
          {loading && <div className="px-3 py-2 text-ink-500">Searching…</div>}
          {!loading &&
            results.map((d) => (
              <button
                key={d._id}
                type="button"
                onClick={() => {
                  navigate(`/devices/${d._id}`);
                  setOpen(false);
                }}
                className="w-full text-left px-3 py-2 rounded-lg hover:bg-ink-900/5 flex flex-col"
              >
                <span className="font-medium">{d.name}</span>
                <span className="text-xs text-ink-500">Click for details</span>
              </button>
            ))}
          {!loading && results.length === 0 && (
            <div className="px-3 py-2 text-ink-500">No matches for &quot;{value.trim()}&quot;</div>
          )}
          <button
            type="button"
            onClick={() => {
              setSuggestOpen(true);
              setOpen(false);
            }}
            className="w-full text-left px-3 py-2 rounded-lg hover:bg-ink-900/5 flex items-center gap-2 border-t border-ink-900/5 mt-1 pt-2 font-medium"
          >
            <span className="w-5 h-5 rounded-full bg-ink-900 text-white flex items-center justify-center text-xs shrink-0">
              +
            </span>
            Add product to our database
          </button>
          <button
            type="button"
            onClick={() => runDetailedSearch(value.trim())}
            className="w-full text-left px-3 py-1.5 text-xs text-ink-500 hover:underline"
          >
            Detailed search
          </button>
        </div>
      )}

      <SuggestDeviceModal
        open={suggestOpen}
        initialName={value.trim()}
        onClose={() => setSuggestOpen(false)}
      />
    </div>
  );
}
